import React, { ChangeEvent, useState } from 'react';
import { useStyles } from './InformationForm.styles';
import { Grid } from '@mui/material';
import { TextField } from '../../Global/TextField/TextField';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton';
import { CheckboxField } from '../../Global/CheckboxField/CheckboxField';
import TextRender from 'components/Global/TextRender/TextRender';
import { IFormData, IInformationForm } from 'interfaces/requestAQuote';
import { trackEvent } from 'helpers/Analytics';
import { useSelector } from 'react-redux';
import { getRAQData } from 'redux/slices/vehicleSlice';
import { useBeforeLeave } from 'hooks/useBeforeLeave';
import { selectZipcode } from 'redux/slices/zipcodeSlice';
import { getADXData } from 'redux/slices/adxSlice';

interface Props {
  stepData: IInformationForm;
  onSubmit?: Function;
  isLoading: boolean;
  isAdxEnhanced: boolean;
}

interface IErrors {
  'first-name'?: IErrorField;
  'last-name'?: IErrorField;
  email?: IErrorField;
  'raq-zipcode'?: IErrorField;
  terms?: IErrorField;
}

interface IErrorField {
  type: string;
  message: string;
  ref: HTMLInputElement;
}
export function InformationForm({
  stepData,
  isLoading,
  onSubmit,
  isAdxEnhanced,
}: Props): JSX.Element {
  const classes = useStyles();
  const [shownErrors, setShownErrors] = useState<IErrors>();
  const raqVehicleData = useSelector(getRAQData);
  const adxVehicleData = useSelector(getADXData);
  const methods = useForm();
  const { handleSubmit, setValue } = methods;
  const zipcode = useSelector(selectZipcode);

  const onUserLeave = () => {
    trackEvent({
      label: `form:raq abandon`,
      category: {
        includePageName: true,
        text: 'request a quote form',
      },
      type: 'cta',
      modelSeries: isAdxEnhanced
        ? adxVehicleData.vehicleModelSeries
        : raqVehicleData.modelSeries || 'no model',
      modelYear: isAdxEnhanced
        ? adxVehicleData.modelYear
        : raqVehicleData.modelYear || 'no model',
      modelId: isAdxEnhanced
        ? adxVehicleData.modelId
        : raqVehicleData.modelId || 'no model',
    });
  };
  useBeforeLeave(onUserLeave);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    e.target.type === 'checkbox'
      ? setValue(name, e.target.checked)
      : setValue(name, value);
  };
  const onSubmitForm: SubmitHandler<FieldValues> = (data) => {
    setShownErrors({});
    if (!isLoading) {
      trackEvent({
        label: `form:raq submit`,
        category: {
          includePageName: true,
          text: 'request a quote form',
        },
        type: 'cta',
        modelSeries: isAdxEnhanced
          ? adxVehicleData.vehicleModelSeries
          : raqVehicleData.modelSeries || 'no model',
        modelYear: isAdxEnhanced
          ? adxVehicleData.modelYear
          : raqVehicleData.modelYear || 'no model',
        modelId: isAdxEnhanced
          ? adxVehicleData.modelId
          : raqVehicleData.modelId || 'no model',
      });
      onSubmit && onSubmit(data as IFormData);
    }
  };

  const onError = (errors: IErrors) => {
    setShownErrors({ ...errors });
    const trackedErrors = ['zipcode', 'email', 'phone'];
    Object.keys(errors).forEach((errorField: string) => {
      if (trackedErrors.includes(errorField)) {
        trackEvent({
          label: `form:raq submit invalid ${errorField}`,
          category: {
            includePageName: true,
            text: 'request a quote form',
          },
          type: 'cta',
          modelSeries: isAdxEnhanced
            ? adxVehicleData.vehicleModelSeries
            : raqVehicleData.modelSeries || 'no model',
          modelYear: isAdxEnhanced
            ? adxVehicleData.modelYear
            : raqVehicleData.modelYear || 'no model',
          modelId: isAdxEnhanced
            ? adxVehicleData.modelId
            : raqVehicleData.modelId || 'no model',
        });
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="information-form"
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit(onSubmitForm, onError)}
        className={classes.form_container}
      >
        <Grid container className={classes.input_group_container}>
          <Grid item xs={12} md={6} className={classes.input_container}>
            <TextField
              label={stepData['first-name']?.label}
              name={stepData['first-name']?.name || 'notFound'}
              isRequired={stepData['first-name']?.required?.message}
              pattern={stepData['first-name']?.pattern}
              maxLength={stepData['first-name']?.maxLength}
              error={shownErrors?.['first-name']?.message}
              dataAutomation={stepData['first-name']?.label}
              type="text"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6} className={classes.input_container}>
            <TextField
              label={stepData['last-name']?.label}
              name={stepData['last-name']?.name || 'notFound'}
              isRequired={stepData['last-name']?.required?.message}
              pattern={stepData['last-name']?.pattern}
              maxLength={stepData['last-name']?.maxLength}
              error={shownErrors?.['last-name']?.message}
              dataAutomation={stepData['last-name']?.label}
              type="text"
              onChange={handleInputChange}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.input_group_container}>
          <Grid item xs={12} md={6} className={classes.input_container}>
            <TextField
              label={stepData.email?.label}
              name={stepData.email?.name || 'notFound'}
              isRequired={stepData.email?.required?.message}
              pattern={stepData.email?.pattern}
              maxLength={stepData.email?.maxLength}
              error={shownErrors?.['email']?.message}
              dataAutomation={stepData.email?.label}
              type="email"
              onChange={handleInputChange}
            />
          </Grid>
          <Grid
            container
            item
            xs={12}
            md={6}
            className={classes.input_group_container}
          >
            <Grid item xs={12} md={6} className={classes.input_container}>
              <TextField
                label={stepData['raq-zipcode']?.label}
                name={stepData['raq-zipcode']?.name || 'notFound'}
                isRequired={stepData['raq-zipcode']?.required?.message}
                pattern={stepData['raq-zipcode']?.pattern}
                maxLength={stepData['raq-zipcode']?.maxLength}
                error={shownErrors?.['raq-zipcode']?.message}
                defaultValue={zipcode?.length > 0 ? zipcode : undefined}
                dataAutomation={stepData['raq-zipcode']?.label}
                type="number"
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6} className={classes.input_container}>
              <TextField
                label={stepData.phone?.label}
                name={stepData.phone?.name || 'notFound'}
                isRequired={stepData.phone?.required?.message}
                pattern={stepData.phone?.pattern}
                maxLength={stepData.phone?.maxLength}
                type="tel"
                dataAutomation={stepData.phone?.label}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.input_container}>
          <TextField
            label={stepData.comments?.label}
            name={stepData.comments?.name || 'notFound'}
            isRequired={stepData.comments?.required?.message}
            pattern={stepData.comments?.pattern}
            maxLength={stepData.comments?.maxLength}
            dataAutomation={stepData.comments?.label}
            type="text"
            onChange={handleInputChange}
          />
        </Grid>
        <CheckboxField
          name={stepData.terms?.name}
          infoText={stepData.terms?.label || 'notFound'}
          isRequired={stepData.terms?.required?.message}
          error={shownErrors?.['terms']?.message}
          dataAutomation={stepData.terms?.name}
          onChange={handleInputChange}
        />
        <LinkButton
          classNames={{ container: classes.submit_button, link: 'test-submit' }}
          variant={LinkButtonType.red}
          type="submit"
          dataAutomation="submit"
        >
          {isLoading ? (
            <span className={`${classes.spinner}`}></span>
          ) : (
            <TextRender text={stepData.submitText} />
          )}
        </LinkButton>
      </form>
    </FormProvider>
  );
}
